import { Container, Graphics, Text, TextStyle } from "pixi.js";
import { useEffect, useRef } from "react";

import { FogRoom } from "@/features/battlemap/types";

interface UseRoomRendererProps {
  layerContainersRef: React.MutableRefObject<Map<string, Container>>;
  rooms: FogRoom[];
  isReady: boolean;
}

export function useRoomRenderer({
  layerContainersRef,
  rooms,
  isReady,
}: UseRoomRendererProps) {
  const roomGraphicsRef = useRef<Graphics | null>(null);
  const labelsRef = useRef<Map<string, Text>>(new Map());

  useEffect(() => {
    if (!isReady) return;

    const roomsContainer = layerContainersRef.current.get("rooms");
    if (!roomsContainer) {
      return;
    }

    if (!roomGraphicsRef.current) {
      const g = new Graphics();
      g.label = "rooms-overlay";
      roomsContainer.addChild(g);
      roomGraphicsRef.current = g;
    }

    const g = roomGraphicsRef.current;
    g.clear();

    if (!rooms) return;

    rooms.forEach((room) => {
      if (!room.visible || !room.bounds || room.bounds.length < 6) return;

      const color = parseInt(room.color.replace("#", ""), 16);

      g.moveTo(room.bounds[0], room.bounds[1]);
      for (let i = 2; i < room.bounds.length; i += 2) {
        g.lineTo(room.bounds[i], room.bounds[i + 1]);
      }
      g.closePath();
      g.fill({ color, alpha: 0.2 });

      const centerX =
        room.bounds.reduce(
          (sum, val, idx) => (idx % 2 === 0 ? sum + val : sum),
          0
        ) /
        (room.bounds.length / 2);
      const centerY =
        room.bounds.reduce(
          (sum, val, idx) => (idx % 2 === 1 ? sum + val : sum),
          0
        ) /
        (room.bounds.length / 2);

      let label = labelsRef.current.get(room.id);
      if (!label) {
        const labelStyle = new TextStyle({
          fontFamily: "Arial",
          fontSize: 14,
          fill: "#ffffff",
          stroke: { color: "#000000", width: 3 },
          align: "center",
        });
        label = new Text({
          text: room.name,
          style: labelStyle,
        });
        label.label = `room-label-${room.id}`;
        roomsContainer.addChild(label);
        labelsRef.current.set(room.id, label);
      }

      label.x = centerX - label.width / 2;
      label.y = centerY - label.height / 2;
      label.visible = room.visible;
    });

    const existingLabelIds = new Set(rooms.map((r) => r.id));
    for (const [id, label] of labelsRef.current) {
      if (!existingLabelIds.has(id)) {
        label.destroy();
        labelsRef.current.delete(id);
      }
    }
  }, [rooms, isReady, layerContainersRef]);

  useEffect(() => {
    return () => {
      if (roomGraphicsRef.current) {
        roomGraphicsRef.current.destroy();
        roomGraphicsRef.current = null;
      }
      for (const label of labelsRef.current.values()) {
        label.destroy();
      }
      labelsRef.current.clear();
    };
  }, []);
}
